/** Installing, enrolling and updating a storage agent: the downloads this PRISM serves and the commands to run. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, KeyRound } from 'lucide-react';
import type { StorageAgentDTO } from '@prism/contracts';
import { api } from '../../../api/client';
import { ErrorBanner } from '../../../components/ErrorBanner';
import { copy } from '../../../lib/clipboard';
import { defaultExportFolder, defaultFolders, folderProblem, guessFile, installSteps, reenrolCommand, targetLabel, updateCommand, updateHint, type AgentDownloadFile, type AgentDownloads } from '../../../lib/agent-install';

/** The agent programs this PRISM serves (/downloads/agent/latest.json and its manifest); null when it has none. */
function useAgentDownloads() {
  return useQuery({
    queryKey: ['agent-downloads'],
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<AgentDownloads | null> => {
      try {
        const latest = await fetch('/downloads/agent/latest.json', { cache: 'no-cache' });
        if (!latest.ok) return null;
        const { version, path } = (await latest.json()) as { version: string; path: string };
        const base = `${window.location.origin}${path.replace(/\/$/, '')}`;
        const m = await fetch(`${base}/manifest.json`, { cache: 'no-cache' });
        if (!m.ok) return null;
        const manifest = (await m.json()) as { files: AgentDownloadFile[] };
        return manifest.files.length ? { version, base, files: manifest.files } : null;
      } catch {
        return null; // not JSON (a development server without the downloads), or unreachable
      }
    },
  });
}

/** A command in its own block with its own copy button. */
function CommandBlock({ id, command }: { id: string; command: string }) {
  return (
    <>
      <pre className="code-block" data-testid={`agent-${id}`} style={{ wordBreak: 'normal', whiteSpace: 'pre', overflowX: 'auto', margin: 0 }}>{command}</pre>
      <button className="btn sm" style={{ alignSelf: 'flex-start' }} data-testid={`agent-copy-${id}`} onClick={() => copy(command)}><Copy size={13} /> Copy</button>
    </>
  );
}

/** Asks (for an active agent) and issues a fresh enrolment token; the agent is signed out until it enrols with it. Null when cancelled. */
export async function issueEnrolmentToken(a: StorageAgentDTO): Promise<string | null> {
  if (a.status === 'active' && !window.confirm('A new enrolment token signs the agent out until it enrols again with the new token. Continue?')) return null;
  const res = await api<{ enrollmentToken: string }>(`/api/v1/agents/${a.id}/enrollment`, { method: 'POST', json: {} });
  return res.enrollmentToken;
}

/**
 * Install, enrol and update instructions. One component for a new agent (token given), for enrolling an existing agent again
 * (token given, reenrol) and for the Install or update dialog (agent given, token null until onCreateToken has made one).
 */
export function EnrollmentInstructions({ token, roots, reenrol, agent, onCreateToken }: { token: string | null; roots: string[]; reenrol?: boolean; agent?: StorageAgentDTO; onCreateToken?: () => Promise<void> }) {
  const origin = window.location.origin;
  const downloads = useAgentDownloads();
  const [tab, setTab] = useState<'file' | 'docker' | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [folders, setFolders] = useState<{ storage: string | null; watch: string | null; exports: string | null; edit: string }>({ storage: null, watch: null, exports: null, edit: '' });
  const files = downloads.data?.files ?? [];
  const reported = agent && 'os' in agent.capabilities ? agent.capabilities.os : undefined;
  const reportedArch = agent && 'arch' in agent.capabilities ? agent.capabilities.arch : undefined;
  const preferred = reported ? files.find((f) => f.os === reported && f.arch === reportedArch) : undefined;
  const file = files.find((f) => `${f.os}-${f.arch}` === target) ?? preferred ?? guessFile(files, navigator.userAgent, navigator.platform);
  const active = tab ?? (files.length ? 'file' : 'docker');
  const defaults = defaultFolders(file?.os ?? 'linux');
  const storage = folders.storage ?? defaults.storage;
  const watch = folders.watch ?? defaults.watch;
  const exportRoot = folders.exports ?? defaultExportFolder(file?.os ?? 'linux');
  const problem = folderProblem(storage, watch, exportRoot, folders.edit);
  const packaging = agent && 'packaging' in agent.capabilities ? agent.capabilities.packaging : undefined;
  const steps = file && downloads.data && !problem ? installSteps({ origin, token, downloadsBase: downloads.data.base, file, storage, watch, exportRoot, editRoot: folders.edit, reenrol: reenrol || Boolean(agent) }) : [];
  const snippet = [
    `PRISM_CONTROL_URL=${origin}`,
    `PRISM_AGENT_ENROLLMENT_TOKEN=${token ?? '<create an enrolment token first>'}`,
    '# Storage folders are chosen in PRISM under this folder:',
    'PRISM_AGENT_STORAGE_ROOT=/srv/prism/storage',
    '# Watch folders are chosen under this one (keep it separate from storage):',
    'PRISM_AGENT_WATCH_ROOT=/srv/prism/watch',
    '# Export folders are chosen under this one (optional; separate from both):',
    'PRISM_AGENT_EXPORT_ROOT=/srv/prism/exports',
    ...(roots.length ? [`PRISM_AGENT_ROOTS=${roots.map((r) => `${r}=/mnt/${r}`).join(',')}`] : []),
    'PRISM_AGENT_STATE_DIR=/var/lib/prism-agent',
  ].join('\n');
  const [creating, setCreating] = useState(false);
  const createToken = onCreateToken && (
    <button className="btn sm" style={{ alignSelf: 'flex-start' }} disabled={creating} data-testid="agent-create-token" onClick={() => { setCreating(true); void onCreateToken().finally(() => setCreating(false)); }}><KeyRound size={13} /> Create enrolment token</button>
  );
  return (
    <>
      {agent && (
        <section data-testid="agent-update-section" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p className="section-label" style={{ margin: 0 }}>Update this agent</p>
          {packaging !== 'binary' ? (
            <p className="muted" style={{ margin: 0 }}>{packaging ? `This agent is installed as ${packaging === 'docker' ? 'a Docker container' : 'source'}: ${updateHint(packaging)}.` : 'This agent has not reported how it is installed. Update a Docker agent by rebuilding the container and a source install with git pull; install the single file below to switch.'}</p>
          ) : (
            <>
              <p className="muted" style={{ margin: 0 }}>Run this on the computer the agent is installed on. It downloads PRISM’s version of the program, checks it, replaces itself and restarts.</p>
              <CommandBlock id="update" command={updateCommand(reported ?? file?.os ?? 'linux')} />
            </>
          )}
        </section>
      )}
      {agent && <p className="section-label" style={{ margin: '8px 0 0' }}>Install on another computer or reinstall</p>}
      {token && <div className="banner warning"><div className="banner-body"><strong>Copy this token now</strong><span>It is shown once, expires in 24 hours, and can be used for a single enrolment.</span></div></div>}
      {agent && token && (
        <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p className="section-label" style={{ margin: 0 }}>Enrol again on the computer it runs on now</p>
          <CommandBlock id="reenrol" command={reenrolCommand({ origin, token, os: reported ?? file?.os ?? 'linux' })} />
        </section>
      )}
      <p className="muted" style={{ margin: 0 }}>The agent exchanges the token for its own credential when it first connects, then checks in every few seconds. It only makes outbound HTTPS connections to PRISM; use <code>NODE_EXTRA_CA_CERTS</code> for a private certificate authority and <code>NODE_USE_ENV_PROXY=1</code> with <code>HTTPS_PROXY</code> behind a proxy.</p>
      <div className="tabs" role="tablist" aria-label="How to run the agent" style={{ padding: 0 }}>
        <button className="tab" role="tab" aria-selected={active === 'file'} data-testid="agent-tab-file" onClick={() => setTab('file')}>Single file (macOS / Linux)</button>
        <button className="tab" role="tab" aria-selected={active === 'docker'} data-testid="agent-tab-docker" onClick={() => setTab('docker')}>Docker</button>
      </div>
      {active === 'file' && (downloads.isLoading ? <div className="skeleton" style={{ height: 120 }} /> : !file || !downloads.data ? (
        <div className="banner" data-testid="agent-no-downloads"><div className="banner-body"><strong>This PRISM has no agent downloads</strong><span>Build them with <code>npm run build:agent</code>, or use the <button className="btn ghost sm" onClick={() => setTab('docker')}>Docker</button> tab.</span></div></div>
      ) : (
        <>
          <div className="field"><label htmlFor="agent-target">Computer</label>
            <select id="agent-target" className="select" data-testid="agent-target" value={`${file.os}-${file.arch}`} onChange={(e) => setTarget(e.target.value)}>
              {files.map((f) => <option key={f.file} value={`${f.os}-${f.arch}`}>{targetLabel(f)}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
            <div className="field"><label htmlFor="agent-storage-folder">Storage folder</label>
              <input id="agent-storage-folder" className="input mono" data-testid="agent-storage-folder" value={storage} spellCheck={false} onChange={(e) => setFolders({ ...folders, storage: e.target.value })} /></div>
            <div className="field"><label htmlFor="agent-watch-folder">Watch folder</label>
              <input id="agent-watch-folder" className="input mono" data-testid="agent-watch-folder" value={watch} spellCheck={false} onChange={(e) => setFolders({ ...folders, watch: e.target.value })} /></div>
            <div className="field"><label htmlFor="agent-export-folder">Export folder</label>
              <input id="agent-export-folder" className="input mono" data-testid="agent-export-folder" value={exportRoot} spellCheck={false} onChange={(e) => setFolders({ ...folders, exports: e.target.value })} /></div>
            <div className="field"><label htmlFor="agent-edit-folder">Edit folder</label>
              <input id="agent-edit-folder" className="input mono" data-testid="agent-edit-folder" value={folders.edit} placeholder="optional" spellCheck={false} onChange={(e) => setFolders({ ...folders, edit: e.target.value })} /></div>
          </div>
          <span className="help" style={{ marginTop: -8 }}>Storage folder: where PRISM-managed storage goes. Watch folder: where watch folders go. Export folder (optional): where export folders go; leave it empty to put them under the storage folder. Edit folder (optional): where Send to edit puts hand-offs for the editors at this site. Storage and watch can be left empty, but not both, and none can be inside another.</span>
          {problem && <ErrorBanner testId="agent-folder-problem"><span>{problem}</span></ErrorBanner>}
          {steps.map((st, i) => (
            <section key={st.id} data-testid={`agent-step-${st.id}`} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p className="section-label" style={{ margin: 0 }}>{i + 1}. {st.title}</p>
              {st.command ? <CommandBlock id={st.id === 'download' ? 'commands' : st.id} command={st.command} /> : createToken ?? <p className="faint" style={{ margin: 0 }}>Needs an enrolment token.</p>}
              <p className="faint" style={{ margin: 0, fontSize: 12 }}>{st.note}</p>
            </section>
          ))}
          {steps.length > 0 && <p className="muted" style={{ margin: 0 }}>When the agent shows as connected, open it here and add its folders: storage folders under the storage folder, watch folders under the watch folder, export folders under the export folder (or the storage folder without one).</p>}
          <p className="faint" style={{ margin: 0, fontSize: 12 }}>Version {downloads.data.version} · SHA-256 of the download: <code data-testid="agent-sha">{file.sha256}</code></p>
        </>
      ))}
      {active === 'docker' && (
        <>
          <p className="muted" style={{ margin: 0 }}>Run the agent next to the storage with these settings.</p>
          {token ? (
            <>
              <pre className="code-block" data-testid="enrollment-snippet">{snippet}</pre>
              <button className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => copy(snippet)}><Copy size={13} /> Copy settings</button>
            </>
          ) : createToken}
          <p className="faint" style={{ margin: 0, fontSize: 12 }}>On a site server with Docker, put these in <code>deploy/agent/.env</code> from the PRISM repository and run <code>docker compose -f deploy/agent/compose.yaml --env-file deploy/agent/.env up --build</code>. See docs/storage-agent.md.</p>
        </>
      )}
    </>
  );
}
